/* global axios */

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

const state = {
  branchStaff: [],
  selectedBranchStaff: null,
  pagination: {
    page: 1,
    page_size: 10,
    total: 0,
    next: null,
  },
  filters: {
    branch_id: null,
    staff_id: null,
    active: null,
  },
  isLoading: false,
  isError: false,
  allBranchStaff: [],
  isAllBranchStaffLoading: false,
};

const getters = {
  getBranchStaff: state => state.branchStaff,
  getSelectedBranchStaff: state => state.selectedBranchStaff,
  getBranchStaffPagination: state => state.pagination,
  formattedPagination: (state) => {
    const { page, total, next, page_size } = state.pagination;
    return {
      currentPage: page,
      totalPages: Math.max(1, Math.ceil(total / page_size)),
      nextPage: next,
      pageSize: page_size,
    };
  },
  getAllBranchStaff: state => state.allBranchStaff,
};

const mutations = {
  SET_BRANCH_STAFF(state, data) { state.branchStaff = data; },
  SET_SELECTED_BRANCH_STAFF(state, data) { state.selectedBranchStaff = data; },
  SET_PAGINATION(state, pagination) { state.pagination = pagination; },
  SET_FILTERS(state, filters) { state.filters = { ...state.filters, ...filters }; },
  SET_IS_LOADING(state, isLoading) { state.isLoading = isLoading; },
  SET_IS_ERROR(state, isError) { state.isError = isError; },
  SET_ALL_BRANCH_STAFF(state, data) { state.allBranchStaff = data; },
  SET_IS_ALL_BRANCH_STAFF_LOADING(state, isLoading) { state.isAllBranchStaffLoading = isLoading; },
};

const actions = {
  async fetchAllBranchStaff({ commit, rootGetters }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_IS_ALL_BRANCH_STAFF_LOADING', true);

      const allResults = [];
      let page = 1;
      const pageSize = 100;
      let hasMore = true;

      while (hasMore) {
        const res = await axios.get(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/branch-staff`),
          { params: { page, page_size: pageSize } }
        );

        const results = res.data.results || [];
        allResults.push(...results);

        hasMore = results.length === pageSize;
        page++;
      }

      commit('SET_ALL_BRANCH_STAFF', allResults);
      return allResults;
    } catch (error) {
      console.error('Fetch all branch staff failed:', error);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_ALL_BRANCH_STAFF_LOADING', false);
    }
  },

  async fetchBranchStaff({ commit, rootGetters, state }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_IS_LOADING', true);
      commit('SET_IS_ERROR', false);

      const { branch_id, staff_id, active } = state.filters;

      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/branch-staff`),
        {
          params: {
            branch_id,
            staff_id,
            active,
            page: state.pagination.page,
            page_size: state.pagination.page_size,
          },
        }
      );

      const { results, page, total, next } = res.data;
      commit('SET_BRANCH_STAFF', results);
      commit('SET_PAGINATION', { ...state.pagination, page, total, next });

      return res.data;
    } catch (error) {
      console.error('Fetch branch staff failed:', error);
      commit('SET_IS_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_LOADING', false);
    }
  },

  async createBranchStaff({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/branch-staff`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Create branch staff failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteBranchStaff({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/branch-staff`),
        { data: payload }
      );
      return res.data;
    } catch (error) {
      console.error('Delete branch staff failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateBranchStaffStatus({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.patch(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/branch-staff/status`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Update branch staff status failed:', error);
      throw error.response?.data || error;
    }
  },

  async bulkAssignStaffToBranch({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/branch-staff/bulk`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Bulk assign staff to branch failed:', error);
      throw error.response?.data || error;
    }
  },

  async bulkAssignBranchesToStaff({ rootGetters }, { staffId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/staff/${staffId}/branches/bulk`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Bulk assign branches to staff failed:', error);
      throw error.response?.data || error;
    }
  },

  async updatePaginationAndFetch({ commit, dispatch, state }, newPagination) {
    try {
      commit('SET_PAGINATION', { ...state.pagination, ...newPagination });
      await dispatch('fetchBranchStaff');
      return true;
    } catch (error) {
      console.error('Update pagination and fetch failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateFiltersAndFetch({ commit, dispatch, state }, filters = {}) {
    try {
      commit('SET_FILTERS', filters);
      commit('SET_PAGINATION', { ...state.pagination, page: 1 });
      await dispatch('fetchBranchStaff');
      return true;
    } catch (error) {
      console.error('Update filters and fetch failed:', error);
      throw error.response?.data || error;
    }
  },

  async clearFiltersAndFetch({ commit, dispatch }) {
    try {
      commit('SET_PAGINATION', { page: 1, page_size: 10, total: 0, next: null });
      commit('SET_FILTERS', { branch_id: null, staff_id: null, active: null });
      await dispatch('fetchBranchStaff');
      return true;
    } catch (error) {
      console.error('Clear filters and fetch failed:', error);
      throw error.response?.data || error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
